/**
 * นำเข้าสต๊อกตั้งต้น — `/v1/initial-stock-imports` (D-032, D-080)
 *
 * สองขั้นตามสัญญา: สร้าง batch + validate (CREATE, ยังไม่แตะสต๊อก) แล้ว post ทั้ง batch (APPROVE) ปริมาณและต้นทุนเป็น string
 */

import { callJson, newIdempotencyKey, type CallResult, type StockFailure, type StockRequestOptions } from './stock';

export const INITIAL_STOCK_IMPORTS_PATH = '/v1/initial-stock-imports';

export interface OpeningStockLineInput {
  readonly storage_location_code: string;
  readonly product_code: string;
  readonly quantity: string;
  readonly uom_code: string;
  readonly unit_cost: string;
  readonly supplier_lot_id?: string;
}

export interface OpeningStockLine {
  readonly line_no: number;
  readonly storage_location_code: string | null;
  readonly product_code: string | null;
  readonly quantity: string;
  readonly uom_code: string;
  readonly unit_cost: string;
  readonly lot_id: string | null;
  readonly validation_error: string | null;
}

export interface OpeningStockBatch {
  readonly batch_id: string;
  readonly plant_code: string;
  readonly status: string;
  readonly line_count: number;
  readonly posted_at: string | null;
  readonly lines: readonly OpeningStockLine[];
}

export type OpeningStockResult =
  | { readonly status: 'ready'; readonly batch: OpeningStockBatch }
  | StockFailure;

function strOrNull(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function req(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function parseOpeningStockLine(raw: unknown): OpeningStockLine | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const location = strOrNull(r.storage_location_code);
  const product = strOrNull(r.product_code);
  const lot = strOrNull(r.lot_id);
  const error = strOrNull(r.validation_error);
  const quantity = req(r.quantity);
  const uom = req(r.uom_code);
  const cost = req(r.unit_cost);
  if (
    typeof r.line_no !== 'number' ||
    location === undefined ||
    product === undefined ||
    lot === undefined ||
    error === undefined ||
    !quantity ||
    !uom ||
    !cost
  ) {
    return null;
  }
  return {
    line_no: r.line_no,
    storage_location_code: location,
    product_code: product,
    quantity,
    uom_code: uom,
    unit_cost: cost,
    lot_id: lot,
    validation_error: error,
  };
}

export function parseOpeningStockBatch(raw: unknown): OpeningStockBatch | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const id = req(r.batch_id);
  const plant = req(r.plant_code);
  const status = req(r.status);
  const posted = strOrNull(r.posted_at);
  if (!id || !plant || !status || posted === undefined || typeof r.line_count !== 'number' || !Array.isArray(r.lines)) {
    return null;
  }
  const lines: OpeningStockLine[] = [];
  for (const entry of r.lines) {
    const line = parseOpeningStockLine(entry);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }
  return { batch_id: id, plant_code: plant, status, line_count: r.line_count, posted_at: posted, lines };
}

function toResult(result: CallResult): OpeningStockResult {
  if (result.status !== 'ok') {
    return result;
  }
  const batch = parseOpeningStockBatch(result.payload);
  return batch === null ? { status: 'invalid-response' } : { status: 'ready', batch };
}

/** `POST /v1/initial-stock-imports` — สร้าง + validate (ไม่ส่ง quality_status; เซิร์ฟเวอร์กำหนดเอง) */
export async function createOpeningStockBatch(
  plantCode: string,
  lines: readonly OpeningStockLineInput[],
  options: StockRequestOptions = {},
): Promise<OpeningStockResult> {
  return toResult(
    await callJson(
      'POST',
      INITIAL_STOCK_IMPORTS_PATH,
      {
        plant_code: plantCode,
        lines: lines.map((line, index) => ({
          line_no: index + 1,
          storage_location_code: line.storage_location_code,
          product_code: line.product_code,
          quantity: line.quantity,
          uom_code: line.uom_code,
          unit_cost: line.unit_cost,
          ...(line.supplier_lot_id ? { supplier_lot_id: line.supplier_lot_id } : {}),
        })),
      },
      {},
      options,
    ),
  );
}

/** `POST …/{batch_id}/post` — all-or-nothing; batch ที่ POSTED แล้วคืนผลเดิม (replay) */
export async function postOpeningStockBatch(
  batchId: string,
  options: StockRequestOptions = {},
): Promise<OpeningStockResult> {
  return toResult(
    await callJson(
      'POST',
      `${INITIAL_STOCK_IMPORTS_PATH}/${encodeURIComponent(batchId)}/post`,
      undefined,
      { 'Idempotency-Key': newIdempotencyKey() },
      options,
    ),
  );
}

/**
 * แยกข้อความที่วาง (TSV จาก Excel หรือ CSV) เป็นบรรทัดนำเข้า — คอลัมน์:
 * ที่เก็บ, รหัสสินค้า, จำนวน, หน่วย, ต้นทุน/หน่วย, [Lot ผู้ขาย]
 * บรรทัดที่ผิดรูปไม่ถูกทิ้งเงียบ ๆ: คืนเป็น `errors` พร้อมเลขบรรทัดให้ผู้ใช้แก้ก่อนส่ง
 */
export function parseOpeningStockText(text: string): {
  readonly lines: readonly OpeningStockLineInput[];
  readonly errors: readonly string[];
} {
  const lines: OpeningStockLineInput[] = [];
  const errors: string[] = [];
  text.split(/\r?\n/).forEach((raw, index) => {
    if (raw.trim() === '') {
      return;
    }
    const cells = raw.split(raw.includes('\t') ? '\t' : ',').map((cell) => cell.trim());
    const [location, product, quantity, uom, cost, lot] = cells;
    if (cells.length < 5 || !location || !product || !quantity || !uom || !cost) {
      errors.push(`แถว ${index + 1}: ต้องมีอย่างน้อย 5 คอลัมน์ (ที่เก็บ, รหัสสินค้า, จำนวน, หน่วย, ต้นทุน/หน่วย)`);
      return;
    }
    lines.push({
      storage_location_code: location.toUpperCase(),
      product_code: product.toUpperCase(),
      quantity,
      uom_code: uom.toUpperCase(),
      unit_cost: cost,
      ...(lot ? { supplier_lot_id: lot } : {}),
    });
  });
  return { lines, errors };
}
